import { useQueries } from '@tanstack/react-query'
import { groupBy, mapValues, sumBy } from 'lodash'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_SINGLE_CHAIN_BALANCES } from '~/constants/refetch-intervals'
import { useActiveOrg } from '~/hooks/org'
import { getSmartAccountBalances } from '~/hooks/useBalances'
import { useServerRequests } from '~/hooks/useRequests'
import { EvmAddress } from '~/utils/address'

/**
 * USD held per chain across the given accounts, for an explicit chain list.
 * `/api/org/balances/v2` only reports enabled networks and the accounting
 * queries only report accounting networks, so a caller that needs a balance
 * outside its own scope asks the per-account endpoint directly. Chains holding
 * under a dollar are dropped, so the result is sparse.
 */
export const useBalanceTotalsByChain = ({
  addresses,
  chainIds,
}: {
  addresses: EvmAddress.Type[]
  chainIds: number[]
}): Record<number, number> => {
  const { serverGetRequest } = useServerRequests()
  const { org } = useActiveOrg()

  const queries = addresses.map((address) => ({
    queryKey: [
      QUERY_KEYS.SMART_ACCOUNT_BALANCES,
      'chainTotal',
      address,
      { version: '2.0', orgId: org?.id, chainIds },
    ],
    queryFn: async (): Promise<Record<number, number>> => {
      const { balances } = await getSmartAccountBalances({
        serverGetRequest,
        address,
        showSpamTokens: false,
        chainIds,
        orgId: org?.id,
      })
      return mapValues(groupBy(balances, 'chainId'), (rows) =>
        sumBy(rows, 'usdValue'),
      )
    },
    refetchInterval: REFETCH_SINGLE_CHAIN_BALANCES,
  }))

  const results = useQueries({
    queries: chainIds.length > 0 ? queries : [],
  })

  const totals = results.reduce<Record<number, number>>((acc, { data }) => {
    Object.entries(data ?? {}).forEach(([chainId, usd]) => {
      acc[Number(chainId)] = (acc[Number(chainId)] ?? 0) + usd
    })
    return acc
  }, {})

  return Object.fromEntries(
    Object.entries(totals).filter(([, usd]) => usd >= 1),
  )
}
